import { useState, type FormEvent } from 'react';
import { Anchor, Button, Card, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';

export interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<unknown>;
}

/** Client-mood login card: email/password fields, submit button, link to password reset. */
export function LoginForm({ onSubmit }: LoginFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(email, password);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setError(t('auth.invalid'));
      } else {
        setError(t('auth.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card w={360}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput
            label={t('auth.email')}
            type="email"
            autoComplete="username"
            required
            withAsterisk={false}
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
          <PasswordInput
            label={t('auth.password')}
            autoComplete="current-password"
            required
            withAsterisk={false}
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Button type="submit" fullWidth loading={submitting}>
            {t('auth.signIn')}
          </Button>
          <Anchor href="/reset-password" size="sm" ta="center">
            {t('auth.forgot')}
          </Anchor>
        </Stack>
      </form>
    </Card>
  );
}
